import type { FilterConfig, FilterOption } from '@/types/filters'

/**
 * LOTE 1: Configurações de filtros para campos essenciais
 * - Nome (text)
 * - Status de Atividade (select) 
 * - Departamento (select)
 * - Cargo (select)
 * - Cidade/Estado (select)
 * - Email (text)
 */

export const LOTE_1_FILTER_CONFIGS: FilterConfig[] = [
  {
    columnId: 'name',
    label: 'Nome',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'endsWith'],
    placeholder: 'Digite o nome do colaborador...',
    category: 'Dados Pessoais'
  },
  {
    columnId: 'activityStatus', 
    label: 'Status de Atividade',
    type: 'select',
    operators: ['equals', 'in', 'notIn'],
    options: [
      { value: 'active', label: 'Ativo' },
      { value: 'ativo', label: 'Ativo' },
      { value: 'inactive', label: 'Inativo' },
      { value: 'inativo', label: 'Inativo' },
      { value: 'desligado', label: 'Desligado' },
      { value: 'afastado', label: 'Afastado' }
    ],
    category: 'Status'
  },
  {
    columnId: 'departmentName',
    label: 'Departamento', 
    type: 'select',
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione o departamento...',
    category: 'Dados Profissionais'
  },
  {
    columnId: 'positionName',
    label: 'Cargo',
    type: 'select', 
    operators: ['equals', 'in', 'notIn', 'contains'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione o cargo...',
    category: 'Dados Profissionais'
  },
  {
    columnId: 'addressCity',
    label: 'Cidade',
    type: 'multiselect',
    operators: ['in', 'notIn'],
    options: [], // Será populado dinamicamente
    placeholder: 'Selecione uma ou mais cidades...',
    category: 'Localização'
  },
  {
    columnId: 'addressState',
    label: 'Estado',
    type: 'multiselect',
    operators: ['in', 'notIn'],
    options: [
      { value: 'AC', label: 'Acre' },
      { value: 'AL', label: 'Alagoas' },
      { value: 'AP', label: 'Amapá' },
      { value: 'AM', label: 'Amazonas' },
      { value: 'BA', label: 'Bahia' },
      { value: 'CE', label: 'Ceará' },
      { value: 'DF', label: 'Distrito Federal' },
      { value: 'ES', label: 'Espírito Santo' },
      { value: 'GO', label: 'Goiás' },
      { value: 'MA', label: 'Maranhão' },
      { value: 'MT', label: 'Mato Grosso' },
      { value: 'MS', label: 'Mato Grosso do Sul' },
      { value: 'MG', label: 'Minas Gerais' },
      { value: 'PA', label: 'Pará' },
      { value: 'PB', label: 'Paraíba' },
      { value: 'PR', label: 'Paraná' },
      { value: 'PE', label: 'Pernambuco' },
      { value: 'PI', label: 'Piauí' },
      { value: 'RJ', label: 'Rio de Janeiro' },
      { value: 'RN', label: 'Rio Grande do Norte' },
      { value: 'RS', label: 'Rio Grande do Sul' },
      { value: 'RO', label: 'Rondônia' },
      { value: 'RR', label: 'Roraima' },
      { value: 'SC', label: 'Santa Catarina' },
      { value: 'SP', label: 'São Paulo' },
      { value: 'SE', label: 'Sergipe' },
      { value: 'TO', label: 'Tocantins' }
    ],
    category: 'Localização'
  },
  {
    columnId: 'email',
    label: 'Email',
    type: 'text',
    operators: ['contains', 'equals', 'startsWith', 'endsWith'],
    placeholder: 'Digite o email...',
    category: 'Dados de Contato'
  }
]

/**
 * Função para gerar opções dinâmicas baseadas nos dados
 */
export const generateDynamicOptions = <T>(
  data: T[],
  accessor: (item: T) => unknown,
  includeCount = false
): FilterOption[] => {
  const valueMap = new Map<string, number>()
  
  data.forEach(item => {
    const rawValue = accessor(item)

    if (Array.isArray(rawValue)) {
      rawValue.forEach(value => {
        if (value && typeof value === 'string') {
          valueMap.set(value, (valueMap.get(value) || 0) + 1)
        }
      })
    } else if (rawValue && typeof rawValue === 'string') {
      valueMap.set(rawValue, (valueMap.get(rawValue) || 0) + 1)
    }
  })
  
  const options = Array.from(valueMap.entries())
    .map(([value, count]) => ({
      value,
      label: value,
      ...(includeCount && { count })
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
  
  return options
}

/**
 * Função para atualizar configurações com opções dinâmicas
 */
export const updateFilterConfigsWithData = <T>(
  configs: FilterConfig[],
  data: T[],
  accessors: Record<string, (item: T) => unknown>
): FilterConfig[] => {
  return configs.map(config => {
    const accessor = accessors[config.columnId]
    if (accessor && config.options?.length === 0) {
      return {
        ...config,
        options: generateDynamicOptions(data, accessor, true)
      }
    }
    return config
  })
}

// Presets padrão para LOTE 1
export const LOTE_1_PRESETS = [
  {
    id: 'colaboradores_ativos_sp',
    name: 'Colaboradores Ativos - São Paulo',
    description: 'Colaboradores ativos na cidade de São Paulo',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade',
        enabled: true
      },
      {
        columnId: 'addressState', 
        operator: 'equals' as const,
        value: 'SP',
        type: 'select' as const,
        label: 'Estado',
        enabled: true
      }
    ],
    isSystem: true
  },
  {
    id: 'novos_colaboradores',
    name: 'Novos Colaboradores',
    description: 'Colaboradores com nome começando com letras específicas',
    filters: [
      {
        columnId: 'activityStatus',
        operator: 'equals' as const,
        value: 'active',
        type: 'select' as const,
        label: 'Status de Atividade', 
        enabled: true
      }
    ],
    isSystem: true
  }
] as const
